import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './hero.css';

// Import des icônes FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import { 
  faLinkedin,
  faTiktok,
  faFacebook,
  faInstagram,
} from '@fortawesome/free-brands-svg-icons';

const HERO = () => {
  // Tableau d'images pour le carrousel
  const images = [
    '/images/15.jpg',
    '/images/12.jpg',
    '/images/3.jpg',
    '/images/13.jpg',
    '/images/16.jpg'
  ];
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Animation de machine à écrire
  const [displayedText, setDisplayedText] = useState('');
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(100);
  
  // Phrases à animer
  const phrases = useMemo(() => [
    "L'Afrique inspire, MOSE RAI GC transforme",
    "Innovation digitale pour l'Afrique",
    "Solutions numériques sur mesure",
    "Excellence technologique africaine"
  ], []);
  // Animation de machine à écrire
  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    
    const typeWriter = () => {
      if (!isDeleting) {
        // Écriture
        if (displayedText.length < currentPhrase.length) {
          setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
          setTypingSpeed(80); // Vitesse d'écriture normale
        } else {
          // Pause avant de commencer à effacer
          setTimeout(() => setIsDeleting(true), 1500);
        }
      } else {
        // Effacement
        if (displayedText.length > 0) {
          setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
          setTypingSpeed(50); // Vitesse d'effacement plus rapide
        } else {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          setTypingSpeed(80); // Réinitialiser la vitesse
        }
      }
    };

    const timer = setTimeout(typeWriter, typingSpeed);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex, typingSpeed, phrases]);

  // Fonction pour ouvrir/fermer la modal
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  // Fonction pour fermer la modal en cliquant à l'extérieur
  const closeModalOnOutsideClick = (e) => {
    e.stopPropagation();
    if (e.target.classList.contains('realisations-modal-overlay')) {
      setIsModalOpen(false);
    }
  };

  // Écouter la touche Échap pour fermer la modal
useEffect(() => {
  if (!isModalOpen) return;

  const handleEscapeKey = (e) => {
    if (e.key === 'Escape') {
      setIsModalOpen(false);
    }
  };

  // Sauvegarder l'état actuel
  const previousOverflow = document.body.style.overflow;
  
  // Appliquer les modifications
  document.addEventListener('keydown', handleEscapeKey);
  document.body.style.overflow = 'hidden';
  
  // Nettoyage
  return () => {
    document.removeEventListener('keydown', handleEscapeKey);
    document.body.style.overflow = previousOverflow;
  };
}, [isModalOpen]);

  // Tableau des réseaux sociaux pour les réalisations
  const socialPlatforms = [
    { 
      name: 'LinkediIn', 
      icon: faLinkedin, 
      url: 'https://www.linkedin.com/company/mose-rai-gc/', 
      description: 'Projets professionnels et réseautage',
      color: '#0057FF'
    },
    { 
      name: 'TikTok', 
      icon:   faTiktok, 
      url: 'https://www.tiktok.com/@moseraiglobalcorp', 
      description: 'Contenu vidéo et tendances',
      color: '#000000'
    },
    { 
      name: 'Facebook', 
      icon: faFacebook, 
      url: 'https://www.facebook.com/moseraiglobalcorp/', 
      description: 'Communauté et actualités',
      color: '#7209dcff'
    },
    { 
      name: 'Instagramm', 
      icon:   faInstagram,  
      url: 'https://www.instagram.com/moseraiglobalcorp/', 
      description: 'Photos et stories de nos réalisations',
      color: '#b81099ff'
    }
  ];


  // Scroll fluide sécurisé
const scrollToSection = (sectionId, e) => {
  if (e) e.preventDefault(); // Ajouter la vérification de e
  const element = document.getElementById(sectionId);
  if (!element) return; // Évite crash

  const headerHeight = 80;
  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

  window.scrollTo({ top: offsetPosition, behavior: 'smooth' });

};

  // Fonction pour passer à l'image suivante (useCallback pour stabiliser la référence)
  const nextImage = useCallback(() => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  }, [images.length]);

  // Fonction pour passer à l'image précédente
  const prevImage = useCallback(() => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  }, [images.length]);

  // Fonction pour aller à une image spécifique
  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  // Auto-play du carrousel
  useEffect(() => {
    let interval;
    
    if (isAutoPlaying) {
      interval = setInterval(() => {
        nextImage();
      }, 4000); // Change d'image toutes les 4 secondes
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAutoPlaying, nextImage]);

  return (
    <>
      <section id="accueil" className="hero">
        {/* Section avec image de fond optionnelle */}
        
        <div className="container">
          <div className="hero-content-wrapper">
            
            {/* Colonne gauche : Contenu texte */}
            <div className="hero-content">
              <div className="hero-badge">
                <span className="badge-text">SOLUTIONS NUMÉRIQUES INNOVANTES</span>
              </div>
              
              <h1 className="hero-title">
                <span className="title-part">Tous vos services</span>
                <span className="title-part highlight_">numériques </span>
                <span className="title-part">à un seul endroit</span>
              </h1>
              <p className='description fst-italic typewriter-text'>
                  {displayedText}
                  <span className="cursor">|</span>
                </p>
              <div className="hero-actions">
                <button className="btn btn-primary">
                  <span className="btn-text" onClick={(e) => scrollToSection('contact', e)}>Commencer un projet</span>
                  <span className="btn-arrow">→</span>
                </button>
                
                <button 
                  className="btn btn-secondary"
                  onClick={toggleModal}
                >
                  <span className="btn-text">Voir nos réalisations</span>
                </button>
              </div>
              
            </div>
            
            {/* Colonne droite : Images/Visualisation avec CARROUSEL */}
            <div className="hero-visual">
              {/* Conteneur du carrousel */}
              <div className="carousel-container">
                {/* Image principale du carrousel */}
                <div className="main-image-container">
                  <div className="image-placeholder">
                    <img 
                      src={images[currentImageIndex]} 
                      alt={`Slide ${currentImageIndex + 1}`} 
                      className="main-hero-image"
                    />
                  </div>
                  <div className="image-frame"></div>
                  
                  {/* Boutons de navigation du carrousel */}
                  <button 
                    className="carousel-btn prev-btn"
                    onClick={prevImage}
                    aria-label="Image précédente"
                  >
                    ‹
                  </button>
                  
                  <button 
                    className="carousel-btn next-btn"
                    onClick={nextImage}
                    aria-label="Image suivante"
                  >
                    ›
                  </button>
                  
                  {/* Indicateurs de pagination */}
                  <div className="carousel-indicators">
                    {images.map((_, index) => (
                      <button
                        key={index}
                        className={`carousel-indicator ${index === currentImageIndex ? 'active' : ''}`}
                        onClick={() => goToImage(index)}
                        aria-label={`Aller à l'image ${index + 1}`}
                      />
                    ))}
                  </div>
                  
                  {/* Contrôles du carrousel */}
                  <div className="carousel-controls">
                    <button
                      className="carousel-control-btn"
                      onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                      aria-label={isAutoPlaying ? "Arrêter le carrousel" : "Démarrer le carrousel"}
                    >
                      {isAutoPlaying ? '⏸️' : '▶️'}
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Éléments flottants avec icônes/images (inchangés) */}
              <div className="floating-element element-1">
                <div className="floating-text">
                  <h5>Applications Mobile</h5>
                  <p>iOS & Android</p>
                </div>
              </div>
              
              <div className="floating-element element-2">
                <div className="floating-text">
                  <h5>Solutions Web</h5>
                  <p>Responsive Design</p>
                </div>
              </div>
              
              <div className="floating-element element-3">
                <div className="floating-text">
                  <h5>Graphisme & Montage</h5>
                  <p>Design</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Indicateur de scroll */}
        <div className="scroll-indicator">
          <div className="mouse">
            <div className="wheel"></div>
          </div>
          <p className="scroll-text">Défiler pour découvrir</p>
        </div>
      </section>

      {/* Modal pour les réalisations */}
      {isModalOpen && (
        <div 
          className="realisations-modal-overlay"
          onClick={closeModalOnOutsideClick}
        >
          <div className="realisations-modal">
            {/* En-tête de la modal */}
            <div className="modal-header">
              <h3 className="modal-title">
                Découvrez nos réalisations
              </h3>
              <button 
                className="modal-close-btn"
                onClick={toggleModal}
                aria-label="Fermer"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Contenu de la modal */}
            <div className="modal-content">
              <p className="modal-subtitle">
                Explorez nos projets et réalisations sur différentes plateformes
              </p>

              {/* Grille des plateformes */}
              <div className="platforms-grid">
                {socialPlatforms.map((platform, index) => (
                  <a
                    key={index}
                    href={platform.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="platform-card"
                    style={{ '--platform-color': platform.color }}
                  >
                    <div className="platform-icon">
                      <FontAwesomeIcon icon={platform.icon} />
                    </div>
                    <div className="platform-info">
                      <h4 className="platform-name">{platform.name}</h4>
                      <p className="platform-description">{platform.description}</p>
                    </div>
                    <div className="platform-arrow">
                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                    </div>
                  </a>
                ))}
              </div>

              {/* Note supplémentaire */}
              <div className="modal-footer">
                <p className="modal-note">
                  <strong>Note :</strong> Ces liens ouvriront nos profils sur les plateformes respectives dans un nouvel onglet.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HERO;